/**
 * @component LogFormComponent
/**
 * Manages the receiving log form, which allows users to input a Purchase Order (PO) number,
 * fetch related PO data, and populate the form with that data. The form's fields dynamically
 * adjust based on whether the PO is for service or delivery, including any default values that
 * are specific to the PO type.
 * 
 * This component also controls Ionic modals for editing associated contacts and handles
 * generating print labels or triggering an email popup with log data. The print and email actions
 * can be triggered at any point, but they are automatically invoked when the form is submitted,
 * at which point the new log entry is recorded in the receiving log table.
 * 
 * Subscriptions:
 * - Various subscriptions are used to fetch and update the data related to the PO, including
 *   details like the job information, location, and service/delivery-related data from the assets data service.
 * - UI elements like the form's field visibility, defaults, and modal popups are updated based on
 *   the fetched data and user interactions.
 * 
 * Notes:
 * - This component interacts with multiple services to fetch data asynchronously and update
 *   the form UI accordingly. Subscriptions are key to managing the real-time changes and data
 *   population, ensuring the form reflects the correct state throughout the user journey.
 * - The component may benefit from refactoring the subscription management to simplify the flow
 *   and improve maintainability, such as combining multiple observables or leveraging operators 
 *   like `combineLatest` for more declarative data handling.
 */

import {
  Component,
  OnInit,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { IonInput } from '@ionic/angular';
import { AlertController } from '@ionic/angular';
import { Data, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ReceivingLogStateService } from '../../../domain/receiving-log/receiving-log-state.service';
import { PoLookupService, PoLookupError } from '../../../domain/receiving-log/po-lookup.service';
import { LogSubmissionService, LogSubmissionError } from '../../../domain/receiving-log/log-submission.service';
import { AssetsDataService } from '../../../service/assets-data.service';
import { ContactsService } from '../../../service/contact.service';
import { EmployeeDirectoryError } from '../../../models/contacts/EmployeeDirectoryError';
import { PhotoService } from '../../../service/photo.service';
import { ToastService } from '../../../service/toast.service';
import { SelectedLocationService } from '../../../service/selected-location.service';
import { ScreenSizeService } from '../../../service/screen-size.service';
import { PrintService, BrowserLabelItems } from '../../../service/print.service';
import { EntraService } from '../../../service/entra.service';
import {
  FormBuilder,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { nonNegativeValidator } from '../../../shared/validators/non-negative-validator';
import {
  derivePoPrefill,
  poFirst6IsNumeric,
  generateNotifyListString as deriveNotifyListString,
  createReceivingLogRow,
  applyServiceSubmissionFields,
  buildEmailData,
  BuildEmailDataFailure,
  buildSingleTypePrintQr,
  buildSingleTypeItemRefs,
  parseItemFacts,
  ParsedItemFacts,
  splitQrPrintRequests,
} from '../../../domain/receiving-log/rules';
import { environment } from '../../../../environments/environment';
import { getPoReportUrl } from '../../../shared/helpers/get-po-report-url';
import { AddReceivingLogResponse } from '../../../models/logs/AddReceivingLogResponse';
import { JobInformation } from '../../../models/poData/JobInformation';
import { NewLog } from '../../../models/logs/NewLog';

import { Location } from '../../../models/assets-data/LocationConfig.interface';
import {
  ReceivingFormInput,
  Option,
  Defaults,
  DefaultSetting,
} from '../../../models/assets-data/receiving/ReceivingLogFormConfig.interface';
import { PrintRequest, PrintKind } from '../../../models/printing/PrintRequest';
import { PrinterInfo } from '../../../models/printing/PrinterInfo';
import { TechZones } from '../../../models/assets-data/TechZonesConfig.interface';

import { resolveContactName } from '../../../domain/receiving-log/rules/contact-insight.rules';
import { findContactInsight } from '../../../domain/receiving-log/rules/contacts.rules';
import { FormattedPoValues } from '../../../models/poData/FormattedPoValues';
import { FormattedContacts } from '../../../models/poData/FormattedContacts';
import { PoEmailData } from '../../../models/poData/FormattedContacts';
import { LocationInformation } from '../../../models/poData/LocationInformation';
import { DataLoading } from '../../../models/DataLoading.interface';
import { LogType } from '../../../models/logs/LogType.enum';
import {
  LoadingText,
  AlertText,
  ToastText,
} from '../../../models/assets-data/UiConfig.interface';
import { SelectedLocation } from '../../../models/selectedLocation.interface';
import { LogEntryPhoto } from '../../../models/logs/photos/LogEntryPhoto.interface';
import { EmailData } from '../../../models/email/EmailData';
import { ApiReceivingLogRow } from '../../../models/logs/ApiReceivingLogRow';
import { User } from '../../../models/User.interface';
import { AddReceivingLogRequest } from '../../../models/logs/AddReceivingLogRequest';
import { EmailPreviewRequest } from '../../../models/email/EmailPreviewRequest';
import { EmailService } from '../../../service/email.service';
import { EmailPreviewResponse } from '../../../models/email/EmailPreviewResponse';
import { nonZeroValidator } from '../../../shared/validators/non-zero-validator';
import { QR_BASE_URL } from '../../../shared/constants/qr-base-url';
import {
  browserQrEnabled as resolveBrowserQrEnabled,
  anyBrowserQrEnabled,
  resolvePrintRoute,
  PrintDoc,
  PrintRoute,
} from '../../../domain/receiving-log/rules/print-path.rules';
import { PrintRouteContextService } from '../../../service/print-route-context.service';

@Component({
  selector: 'app-log-form',
  templateUrl: './log-form.component.html',
  styleUrls: ['./log-form.component.css'],
})
export class LogFormComponent implements OnInit, OnDestroy {
  @ViewChild('inputElement', { static: false }) inputElement!: IonInput;
  @ViewChild('previewLogModal') previewLogModal!: any;

  //---------------------- Services ---------------------------------------------//



  private _toastService: ToastService; // Service for toast notifications
  private _printService: PrintService; // Service to handle printing
  private _entraService: EntraService; // Service for authentication-related tasks
  private _screenSizeService: ScreenSizeService; // Service for managing screen size detection
  private _receivingLogService:ReceivingLogStateService; // Service for logging actions
  private _assetsDataService: AssetsDataService; // Service for asset data management
  private _contactsService: ContactsService; // Service to handle contact information
  private _alertController: AlertController; // Service for displaying alerts
  private _emailService: EmailService; // Service for email-related tasks
  private _photoService: PhotoService; // Service for photo-related tasks
  private _selectedLocationService: SelectedLocationService; // Service for managing selected location
  private _poLookupService: PoLookupService; // Stateless PO validation + data lookup
  private _logSubmissionService: LogSubmissionService; // Stateless log submission
  private _printRouteContext: PrintRouteContextService; // Builds the shared print route context

  //---------------------- Subscriptions ---------------------------------------------//

  // Subscriptions for data streams from services that fetch and update data through the backend API
  public photosSubscription!: Subscription; // Subscription for photo data updates
  public printersSubscription!: Subscription; // Live printer list for the backend path
  public printersLoadingSubscription!: Subscription; // Printer-list fetch state
  public dataLoadingSubscription!: Subscription; // Subscription for data loading status updates
  public employeeEmailsSubscription!: Subscription; // Subscription for employee email updates

  // Subscriptions for user-controlled data streams
  public locationSubscription!: Subscription; // Subscription for location data changes
  public typeControlSubscription!: Subscription; // Subscription for type control changes (e.g., print type)
  public selectedPrintTypeSubscription!: Subscription; // Subscription for selected print type changes

  // Subscriptions for screen size and user info changes
  public isLargeScreenSubscription!: Subscription; // Subscription for large screen detection
  public isSmallScreenSubscription!: Subscription; // Subscription for small screen detection
  public userSubscription!: Subscription; // Subscription for username changes

  //---------------------- General Data and State Management---------------------------------------------//

  public locations!: Location[]; // List of locations for assets
  public selectedLocation: SelectedLocation = {
    name: 'norfolk',
    label: 'Norfolk',
    laydownSpaceAvailable: false,
  }; // Location info for the receiving log
  public employeeEmails: string[] = new Array<string>(); // Array to store employee emails

  public dataLoading: DataLoading = { isLoading: false, message: '' }; // Data loading state and message


  // Flags to determine screen size for responsive design
  public isLargeScreen!: boolean; // Boolean indicating if the screen is large
  public isSmallScreen!: boolean; // Boolean indicating if the screen is small

  // Placeholder username for the component
  public username: string = 'Test User'; // Default username value
  public userEmail: string = ''; // Default username value
  public assetsDataLoaded = false; // Boolean flag for asset data loading status

  // Flags for tracking the open state of various modals
  public isLogModalOpen = false; // Indicates if the log modal is open
  public isAddContactModalOpen = false; // Indicates if the add contact modal is open
  public isPrintModalOpen = false; // Indicates if the print modal is open
  public isLocaitonInfoModalOpen = false; // Indicates if the location info modal is open
  public isPhotoManagerModalOpen = false; // Indicates if the photo manager modal is open
  public isEmailPreviewModalOpen = false;
  public isPreviewLogModalOpen = false;


  //----------- UI Text configurations ---------------------
  public alertText!: AlertText;
  public toastText!: ToastText;
  public loadingText!: LoadingText; // Text for loading state

  //------------------------Fetched PO Data---------------------------------------------
  public poData: FormattedPoValues = new FormattedPoValues(); // Formatted PO data for display
  public jobInformation: JobInformation = new JobInformation(); // Information related to the job
  public allPoContactData!: FormattedContacts; // Formatted contacts data for the PO
  public zoneInfo!: TechZones; // Tech zone information for locations
  public locationInfo: LocationInformation = new LocationInformation(); // Detailed location info

  //-----------Form and Input Management  ---------------------

  // Form Inputs and Fields:
  public logReceivingFormInputs!: Record<string, ReceivingFormInput>; // Form inputs for the log form
  public poFormField!: ReceivingFormInput; // Purchase order form input field
  public poFormFieldKey!: string; // Key for purchase order form field

  // Form groups
  public logForm!: FormGroup; // Form group for the log form
  public poNumberForm!: FormGroup; // Form group for the PO number form

  // Form controls
  public poNumberControl!: FormControl; // Form control for PO number input
  public printTypeControl!: FormControl; // Form control for print type selection
  public typeControl!: FormControl; // Form control for PO type selection

  //-----------Form Configuration and Settings ---------------------

  // Form Defaults and Validation:
  public typeDefaults!: Defaults; // Default settings for PO types
  public requiredPoDigits!: number; // Number of digits required for the PO number
  public labelPlacement!: string; // Label placement settings for printing

  // Flags for project or service type of PO
  public isProject = false; // Indicates if the PO is for a project
  public isService = true; // Indicates if the PO is for service

  // Flags for PO data entry status
  public poValueEntered = false; // Indicates if a PO value has been entered
  public poValue!: string; // The entered PO value
  public poValueHasLetters = false; // Flag to determine if the PO number has letters
  public showPoTypeToggle = false; // Flag to determine if the PO type toggle is shown

  // Printing options and behavior
  public printTypeOptions: Option[] = [
    { value: 'dymo', label: 'Label' },
    { value: 'fullSheet', label: 'Copier Sheet' },
    { value: 'both', label: 'Both' },
  ];

  public showDymoPrintQty = false; // Flag to show Dymo print quantity
  public showFullSheetPrintQty = false; // Flag to show full sheet print quantity
  public showBothPrintQty = false; // Flag to show both print quantities
  public printingChecked = false; // Indicates if printing is enabled
  public printType!: string; // The selected print type (Dymo, full sheet, etc.

  // Print configuration modal (opened from the Print button): type and quantities.
  public isPrintConfigModalOpen = false;
  public selectedZebraPrinter: string = ''; // Label (Zebra) printer — backend path only
  public selectedCopierPrinter: string = ''; // Copier (sheet) printer — backend path only

  /** The live printer list, for the named-printer selects on the backend path. */
  public availablePrinters: PrinterInfo[] = [];
  public printersLoading: boolean = false;

  //Notification and Contact Management
  public notifyList: PoEmailData[] = []; // List of email data objects for notifications
  public notifyListString: string = ''; // Comma-separated string of emails in the notify list
  public primaryContact: string = ''; // Name of the primary contact for the PO
  public primaryContactEmail: string = ''; // Email address of the primary contact
  public shelfLocation!: string; // Shelf location info for the receiving log

  //-------------------------New Log----------------------------------------------
  
  public addLogRequest!: AddReceivingLogRequest;// Request data for log creation     
  public addLogResponse: AddReceivingLogResponse =
    new AddReceivingLogResponse(); // Response data for log creation
  public newLog: NewLog = new NewLog(); // New log entry object
  public logEntryPhotos: LogEntryPhoto[] = []; // Photos associated with the log entry
  public photosDisabled: boolean = false; // Flag to determine if photos are included in the log entry
  public emailsDisabled: boolean = false; // Flag to determine if emails are included in the log entry
  public includePhotosInEmail: boolean = true; // Flag to determine if photos are included in the email preview


 
  // Initializes the component, injects services, and loads initial data
  constructor(
   receivingLogService:ReceivingLogStateService,
    formBuilder: FormBuilder,
    assetsDataService: AssetsDataService,
    contactsService: ContactsService,
    toastService: ToastService,
    screenSizeService: ScreenSizeService,
    printService: PrintService,
    entraService: EntraService,
    router: Router,
    alertController: AlertController,
    emailService: EmailService,
    photoService: PhotoService,
    selectedLocationService: SelectedLocationService,
    poLookupService: PoLookupService,
    logSubmissionService: LogSubmissionService,
    printRouteContextService: PrintRouteContextService

  ) {
    this._printRouteContext = printRouteContextService;
    this._toastService = toastService;
    this._receivingLogService =receivingLogService;
    this._contactsService = contactsService;
    this._assetsDataService = assetsDataService;
     this._receivingLogService =receivingLogService;
    this._screenSizeService = screenSizeService;
    this._entraService = entraService;
    this._printService = printService;
    this._alertController = alertController;
    this._emailService = emailService;
    this._photoService = photoService;
    this._selectedLocationService = selectedLocationService;
    this._poLookupService = poLookupService;
    this._logSubmissionService = logSubmissionService;
    this.isLargeScreen = this._screenSizeService.isLargeScreen.value;
    this.isSmallScreen = this._screenSizeService.isSmallScreen.value;

   
    // Company emails are fetched once per app session (warmed at login) — this
    // is a cache read unless the app-load prefetch has not landed yet.
    // `employeeEmails` is kept current by the fetchedCompanyEmails subscription in
    // setSubscriptions() — this only makes sure the list has been requested.
    // The service toasts the failure itself; this handler just keeps a failed
    // fetch from surfacing as an unhandled rejection.
    this._contactsService.ensureCompanyEmails().subscribe({
      error: (err) => console.error('Employee directory unavailable:', err),
    });



    // Fetch assets data (locations, zones, UI config, Receiving Log form data)
    this.getLocationData();
    this.getZoneData();
    this.getUiConfig();
    this.getReceivingLogFormData();
  }

  // Initializes component state, sets up subscriptions, and resets the form
  ngOnInit() {
    this.setSubscriptions();
    if (this.logForm) {
      this.logForm.reset();
    }
    this._photoService.clearLogEntryPhotos(); 
  }

  // Cleans up subscriptions and resets form when the component is destroyed
  ngOnDestroy() {
    if (this.dataLoadingSubscription)
      this.dataLoadingSubscription.unsubscribe();
    if (this.typeControlSubscription)
      this.typeControlSubscription.unsubscribe();
    if (this.employeeEmailsSubscription)
      this.employeeEmailsSubscription.unsubscribe();
    if (this.locationSubscription) this.locationSubscription.unsubscribe();
    if (this.isLargeScreenSubscription)
      this.isLargeScreenSubscription.unsubscribe();
    if (this.isSmallScreenSubscription)
      this.isSmallScreenSubscription.unsubscribe();
    if (this.userSubscription) this.userSubscription.unsubscribe();
    if (this.selectedPrintTypeSubscription)
      this.selectedPrintTypeSubscription.unsubscribe();
    if (this.printersSubscription) this.printersSubscription.unsubscribe();
    if (this.printersLoadingSubscription)
      this.printersLoadingSubscription.unsubscribe();
    if (this.logForm) this.logForm.reset();
  }

  //--------------------------------------------------------------------------------------------------------------------------------------------//
  // Methods for component initialization

  // Fetches and assigns location data from the assets data service.This will be used to configure location-specific data
  public getLocationData(): void {
    this.locations = this._assetsDataService.GetLocationData();
  }

  // Fetches and assigns tech zone information from the assets data service. This will be used to determine the shelf location for POs of type "Service"
  public getZoneData(): void {
    const zoneData = this._assetsDataService.GetTechZoneData();
    this.zoneInfo = zoneData;
  }

  // Fetches receiving log form data and updates component properties accordingly.
  // The ReceivingFormInput data is then used to configure the log form fields
  public getReceivingLogFormData(): void {
    const logFormData = this._assetsDataService.GetReceivingLogFormData();
    if (logFormData) {
      this.assetsDataLoaded = true;
      this.requiredPoDigits = logFormData.requiredPoDigits;
      this.typeDefaults = logFormData.defaults;
      this.setLogFormConfig(logFormData.inputs);
    }
  }

  // Loads and applies UI configuration (label placement, modals, toast messages)
  public getUiConfig(): void {
    const uiConfigData = this._assetsDataService.GetUiConfigData();
    this.labelPlacement = uiConfigData.labelPlacement.logForm;
    this.toastText = uiConfigData.toastText;
    this.loadingText = uiConfigData.loadingText;
    this.alertText = uiConfigData.alertText;
  }

  // Subscribes to multiple data streams from various services and updates component properties accordingly.
  public setSubscriptions(): void {
    // Unsubscribe from existing subscriptions to prevent memory leaks
    if (this.isLargeScreenSubscription) {
      this.isLargeScreenSubscription.unsubscribe();
    }
    if (this.isSmallScreenSubscription) {
      this.isSmallScreenSubscription.unsubscribe();
    }
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    if (this.locationSubscription) {
      this.locationSubscription.unsubscribe();
    }
    if (this.dataLoadingSubscription) {
      this.dataLoadingSubscription.unsubscribe();
    }
    if (this.employeeEmailsSubscription) {
      this.employeeEmailsSubscription.unsubscribe();
    }
    if (this.photosSubscription) {
      this.photosSubscription.unsubscribe();
    }
    if (this.printersSubscription) {
      this.printersSubscription.unsubscribe();
    }
    if (this.printersLoadingSubscription) {
      this.printersLoadingSubscription.unsubscribe();
    }

    // The live printer list feeds the named-printer selects in the print-config
    // modal. Subscribed here rather than on open so a list already fetched this
    // session is on screen the moment the modal appears; the fetch itself is
    // still deferred to openPrintConfig, and only on the backend path.
    this.printersSubscription = this._printService.availablePrinters.subscribe(
      (printers: PrinterInfo[]) => {
        this.availablePrinters = printers;
      }
    );
    this.printersLoadingSubscription = this._printService.printersLoading.subscribe(
      (loading: boolean) => {
        this.printersLoading = loading;
      }
    );

    // Subscribe to large screen size changes and update `isLargeScreen` property.
    //This will be used to adjust the form layout based on screen size
    this.isLargeScreenSubscription =
      this._screenSizeService.isLargeScreen.subscribe(
        (isLargeScreen: boolean) => {
          this.isLargeScreen = isLargeScreen;
        }
      );

    // Subscribe to small screen size changes and update `isSmallScreen` property.
    //This will be used to adjust the form layout based on screen size
    this.isSmallScreenSubscription =
      this._screenSizeService.isSmallScreen.subscribe(
        (isSmallScreen: boolean) => {
          this.isSmallScreen = isSmallScreen;
        }
      );

    // Subscribe to username changes and update `username` property.
    //This will be used to display the logged-in user's name on any form submissions
    this.userSubscription = this._entraService.user.subscribe((user: User) => {
      this.userEmail = user.email;
      this.username = user.name;
    });

    // Subscribe to selected location changes and update `selectedLocation` property.
    //This will be used to populate the storage facility field in new receiving logs
    this.locationSubscription = this._selectedLocationService.selectedLocation$.subscribe(
      (location: SelectedLocation) => {
        this.selectedLocation = location;
      }
    );

    // Subscribe to data loading status and update `dataLoading` property.
    //This will be used to display loading indicators and messages during data fetches
    this.dataLoadingSubscription = this._receivingLogService.dataLoading.subscribe(
      (dataLoading: DataLoading) => {
        this.dataLoading = dataLoading;
      }
    );

    // Subscribe to fetched company emails and update `employeeEmails` property.
    //This will be used to populate the drop down of company contacts in the child add-contact component
    this.employeeEmailsSubscription =
      this._contactsService.fetchedCompanyEmails.subscribe(
        (contacts: string[]) => {
          this.employeeEmails = contacts;
        }
      );

    // Subscribe to photos changes and update `logEntryPhotos` property.
    this.photosSubscription = this._photoService.logEntryphotos$.subscribe(
      (photos: LogEntryPhoto[]) => {
        this.logEntryPhotos = photos;
      }
    );
  }

  /**
   * Processes the provided Purchase Order (PO) data and updates the component's properties and form controls.
   *
   * This function extracts relevant information from the PO data, such as job details, contact info,
   * location data, and determines the appropriate shelf location and primary contact. It also formats the
   * contact list and generates a notification string. The form is then reset and populated with the extracted
   * PO data, including job information and contact details. Based on the job type (Project/Service),
   * the form is further populated with specific values for project or service-related details.
   *
   * @param poData - The formatted PO data containing job, contact, and location information.
   */
  public processPOData(poData: FormattedPoValues): void {
    // Derive shelf location, contacts, log type, and form values from the PO data
    const prefill = derivePoPrefill(
      poData,
      this.zoneInfo,
      this.poValue,
      this.poValueHasLetters
    );

    // Update component properties with the fetched data
    this.allPoContactData = poData.contactInformation;
    this.locationInfo = poData.locationInformation;
    this.primaryContact = prefill.primaryContact;
    this.shelfLocation = prefill.shelfLocation;
    this.notifyListString = prefill.notifyListString;
    this.notifyList = prefill.notifyList;

    // Reset the form and populate it with the fetched PO data.
    this.logForm.reset();
    this.logForm.controls['poNumber']?.setValue(prefill.formValues.poNumber);
    this.logForm.controls['vendor']?.setValue(prefill.formValues.vendor);
    this.logForm.controls['jobNumber']?.setValue(prefill.formValues.jobNumber);
    this.logForm.controls['jobName']?.setValue(prefill.formValues.jobName);
    this.logForm.controls['jcDept']?.setValue(prefill.formValues.jcDept);
    this.logForm.controls['notify']?.setValue(prefill.formValues.notify);
    this.logForm.controls['contact']?.setValue(prefill.formValues.contact);
    this.logForm.controls['printLabel']?.setValue(true);

    // Update the printing flag to enable print button
    this.printingChecked = true;

    // Set form values based on job type (Project/Service).
    if (prefill.logType === LogType.Project) {
      this.logForm.controls['type'].setValue('Project');
      this.setProjectInitialValues();
    } else if (prefill.logType === LogType.Service) {
      this.logForm.controls['type'].setValue('Service');
      this.logForm.controls['shelfLocation']?.setValue(prefill.shelfLocation);
      this.setServiceInitialValues();
    }

    // Mark all controls as touched to trigger validation feedback
    this.logForm?.markAllAsTouched();
  }

  /**
   * Configures the log form with input settings and validators based on the provided form configuration.
   *
   * This function sets up the form with fields and their options from the provided configuration.
   * It also handles setting up form control subscriptions and validation logic:
   * - Sets up the 'poNumber' form field with required validators (min, max length, and pattern).
   * - Subscribes to changes in the 'printType' field to update print options (Label, Copier Sheet, or both).
   * - Initializes controls for 'printQtyDymo', 'printQtySheet', and other related fields.
   *
   * This function is called within getReceivingLogFormData
   *
   * @param logReceivingFormInputs - A record containing configuration details for the form fields.
   */
  public setLogFormConfig(logReceivingFormInputs: Record<string, ReceivingFormInput>): void {

  

    // Extract the 'printType' options from the provided configuration (e.g., options for label or sheet printing)
    const printTypeOptions = logReceivingFormInputs['printType']['options'];

    // Extract the configuration for the 'poNumber' field
    const poFormFieldKey = 'poNumber';
    const poFormField = logReceivingFormInputs[poFormFieldKey];
    // If printTypeOptions exists, store it in the component property
    if (printTypeOptions) this.printTypeOptions = printTypeOptions;

    this.poFormFieldKey = poFormFieldKey;
    this.poFormField = poFormField;
    this.logReceivingFormInputs = logReceivingFormInputs;

    // Initialize a new FormBuilder to create a new form group based on the provided inputs
    const filterFormBuilder = new FormBuilder();

    // Set validators for the 'poNumber' field:
    // - Required
    // - Minimum and maximum length
    // - Only numeric values allowed
    this.logForm = this.createNewLogFormGroup(logReceivingFormInputs, filterFormBuilder);
    this.logForm.controls['poNumber']?.setValidators([
      Validators.required,
      Validators.minLength(this.requiredPoDigits),
      //Validators.maxLength(this.requiredPoDigits),
      //Validators.pattern('^[0-9]*$'),
    ]);

    // Manually trigger validation for the 'poNumber' control after applying the validators
    this.logForm.controls['poNumber']?.updateValueAndValidity();
    // Set default values for other fields
    this.logForm.controls['printLabel']?.setValue(true);
    this.logForm.controls['printQtyDymo']?.setValue(2);
    this.logForm.controls['printQtySheet']?.setValue(1);

    // Initialize form controls (e.g., 'poNumber', 'type', and 'printType') and ensure they are properly typed
    // Use FormControl if the control is undefined or not already initialized
    this.poNumberControl = this.logForm.get('poNumber')
      ? (this.logForm.get('poNumber') as FormControl)
      : new FormControl();
    this.typeControl = this.logForm.get('type')
      ? (this.logForm.get('type') as FormControl)
      : new FormControl();
    this.printTypeControl = this.logForm.get('printType')
      ? (this.logForm.get('printType') as FormControl)
      : new FormControl();

    // Unsubscribe from any previous subscriptions to avoid memory leaks
    if (this.selectedPrintTypeSubscription)
      this.selectedPrintTypeSubscription.unsubscribe();
    // Subscribe to changes in the 'printType' field to dynamically adjust print options
    // Based on the selected print type, toggle visibility of relevant fields (e.g., Dymo, full sheet, or both print quantities)
    this.selectedPrintTypeSubscription =
      this.printTypeControl?.valueChanges.subscribe((value: string) => {
        // The control value is the selected option's label. Compare against the
        // option array (label = "Label", "Copier Sheet", "Both") rather than
        // hardcoded literals so renaming the labels only touches the config.
        const labelOnly = this.printTypeOptions[0].label;
        const sheetOnly = this.printTypeOptions[1].label;
        const both = this.printTypeOptions[2].label;

        // Update visibility of print quantity fields based on selected print type
        if (value === labelOnly) {
          this.showDymoPrintQty = true;
          this.showFullSheetPrintQty = false;
          this.showBothPrintQty = false;
          this.toggleSheetQtyValidator(false);
          this.toggleDymoQtyValidator(true);
        }
        if (value === sheetOnly) {
          this.showDymoPrintQty = false;
          this.showFullSheetPrintQty = true;
          this.showBothPrintQty = false;
          this.toggleSheetQtyValidator(true);
          this.toggleDymoQtyValidator(false);
        } else if (value === both) {
          this.showDymoPrintQty = true;
          this.showFullSheetPrintQty = true;
          this.showBothPrintQty = true;
          this.toggleSheetQtyValidator(true);
          this.toggleDymoQtyValidator(true);
        }
      });
  }





  /**
   * Creates a new FormGroup for the log form based on the provided data and FormBuilder.
   * This method is used in the `setLogFormConfig` method to initialize the log form with the necessary controls and validators.
   *
   * @param data - The configuration data for the form fields.
   * @param fb - The FormBuilder instance used to create the form controls.
   * @returns A FormGroup with the configured form controls.
   */
  public createNewLogFormGroup(data: any, fb: FormBuilder): FormGroup {
    const formControls: { [key: string]: FormControl } = {};

    Object.keys(data).forEach((key) => {
      const field = data[key]; // Grab the value associated with the key

      const startingValue = this.newLog[key as keyof NewLog];
      const isNumericField = field.inputType === 'number';
      const validators = field.required ? [Validators.required] : [];
      if (isNumericField) {
        validators.push(nonNegativeValidator());
      }
      formControls[key] = fb.control(startingValue, validators);
    });

    return fb.group(formControls);
  }

  //--------------------------------------------------------------------------------------------------------------------------------------------//
  // Methods to update the form based on user interactions

  /**
   * Sets the initial values for the form controls when the job type is "Project".
   * This method is used on component initialization and in the `onJobTypeChange` method to update the form controls based on the selected job type.
   */
  public setProjectInitialValues(): void {
    this.isProject = true;
    this.isService = false;
    const projectDefaults = this.typeDefaults['project'];
    for (const key in projectDefaults) {
      // Use "for...of" to iterate over array elements
      const control = this.logForm.controls[key as keyof DefaultSetting];
      if (control) {
        control.setValue(projectDefaults[key as keyof DefaultSetting]);
      } else {
        console.warn(`Control for key "${key}" not found.`);
      }
    }
  }

  /**
   * Sets the initial values for the form controls when the job type is "Service".
   * This method is used on component initialization and in the `onJobTypeChange` method to update the form controls based on the selected job type.
   */
  public setServiceInitialValues(): void {
    this.isProject = false;
    this.isService = true;
    const serviceDefaults = this.typeDefaults['service'];
    // Loop through the keys of the service defaults and set values for corresponding form controls
    for (const key in serviceDefaults) {
      if (serviceDefaults.hasOwnProperty(key)) {
        const control = this.logForm.controls[key as keyof DefaultSetting];
        if (control) {
          control.setValue(serviceDefaults[key as keyof DefaultSetting]);
        } else {
          console.warn(`Control for key "${key}" not found.`);
        }
      }
    }
    this.logForm.controls['shelfLocation']?.setValue(this.shelfLocation);
  }

  /**
   * Handles the submission of the PO number.
   * This method is used in the template to validate and process the entered PO number.
   * It performs the following steps:
   * 1. Removes focus from the input element
   * 2. Retrieves the entered PO number value and assigns it to `poValue`.
   * 3. Validates the PO number control:
   *    - If valid, sets `poValueEntered` to true, validates the PO number via the log service, and opens the log modal.
   *    - If invalid, marks the control as touched to trigger validation feedback, resets the form, and shows a warning message.
   */
  public onSubmitPo(): void {
    // Remove focus from the input element
    this.inputElement.getInputElement().then((input) => input.blur());

    // Retrieve the entered PO number value
    const enteredPoValue = this.poNumberControl.value;
    this.poValue = enteredPoValue.toString();

    // Check if the first 6 characters can be a number
    const isFirst6Numeric = poFirst6IsNumeric(this.poValue);

    // Validate the PO number control
    if (this.poNumberControl.valid && isFirst6Numeric) {
      this.poValueEntered = true;
      // Quiet warm-up: a new log is starting, and an empty directory here means
      // the add-contact picker and the submit-time name resolution are both
      // about to come up blank. No blocking UI — this only re-attempts a fetch
      // that has already failed once this app session.
      if (this._contactsService.fetchedEmployees.value.length === 0) {
        this._contactsService.ensureDirectoryPopulated().subscribe({
          error: (err) => console.error('Employee directory unavailable:', err),
        });
      }
      // Validate the PO number and fetch its data via the PO lookup service.
      // The lookup runs two backend calls; keep the spinner text on the current phase.
      this._poLookupService.lookup(this.poValue, (phase) => {
        this._receivingLogService.dataLoading.next({
          isLoading: true,
          message: phase === 'validating' ? this.loadingText.validatingPO : this.loadingText.fetchingPOData,
        });
      }).subscribe({
        next: (poData: FormattedPoValues) => {
          this._receivingLogService.dataLoading.next({ isLoading: false, message: '' });
          this.poData = poData; // Store PO data for later use.
          this.processPOData(poData); // Process the PO data and update the form accordingly.
        },
        error: (err: PoLookupError) => {
          console.log(err.cause ?? err);
          this._receivingLogService.dataLoading.next({ isLoading: false, message: '' });
          this._toastService.showWarningMessage(err.userMessage);
        },
      });
    } else {
      // If invalid, mark all controls as touched to trigger validation feedback
      this.poValueEntered = false;
      this.logForm.reset();
      this.poNumberControl.setValue(enteredPoValue);
      this.poNumberControl.markAllAsTouched();
      let errorMsg = this.toastText.logForm.invalidPOInputError;
      // Show a warning message if there is a custom error message
      if (this.poFormField.errorMsg) {
        errorMsg = this.poFormField.errorMsg;
        this._toastService.showWarningMessage(errorMsg);
      }
    }
  }

  /**
   * Toggles the PO type popover.
   * This method is used in the template to toggle the PO type popover.
   */
  public togglePoTypePopover(): void {
    this.showPoTypeToggle = !this.showPoTypeToggle;
  }

  /**
   * Toggles the PO value to have letters or not.
   * This method is used in the template to toggle the PO value to have letters or not.
   */
  public togglePoValueHasLetters(): void {
    this.poValueHasLetters = !this.poValueHasLetters;
    this.showPoTypeToggle = false; // Hide the toggle after changing the value
    // Reset the PO number input when toggling
    const currentValue = this.poNumberControl.value;
    if (currentValue) {
      this.poNumberControl.setValue(currentValue.toString());
    }
  }
  public async showPoTypeAlert() {

    const alert = await this._alertController.create({
      header: this.alertText.poNumberType.title,
      message: this.alertText.poNumberType.message,
      buttons: [this.alertText.poNumberType.closeButton],
      inputs: [
        {
          type: 'checkbox',
          name: 'hasLetters',
          checked: this.poValueHasLetters,
          label: this.alertText.poNumberType.checkboxLabel,
          handler: (value) => {
            if (value.checked == undefined) {
              value.checked = false;
            }
            this.poValueHasLetters = value.checked;
            // Reset the PO number input when toggling
            const currentValue = this.poNumberControl.value;
            if (currentValue) {
              this.poNumberControl.setValue(currentValue.toString());
            }
          },
        },
      ],
    });
    await alert.present();
  }



    /* 
  * Update requirement of sheet quantity 
   */
  public toggleSheetQtyValidator(qtyRequired: boolean): void {
    if (this.logForm) {
      if (qtyRequired) {
        this.logForm.controls['printQtySheet']?.setValidators([Validators.required, nonZeroValidator(), nonNegativeValidator()]);
      } else {
        this.logForm.controls['printQtySheet']?.clearValidators();
      }
      this.logForm.controls['printQtySheet']?.updateValueAndValidity();
    }

  }

  /*
    * Update requirement of dymo quantity
    */
  public toggleDymoQtyValidator (qtyRequired: boolean): void {
    if (this.logForm) {
      if (qtyRequired) {
        this.logForm.controls['printQtyDymo']?.setValidators([Validators.required, nonZeroValidator(), nonNegativeValidator()]);
      } else {
        this.logForm.controls['printQtyDymo']?.clearValidators();
      } 
      this.logForm.controls['printQtyDymo']?.updateValueAndValidity();
    }

  }


  /**
   * Resets the log form,retains the current PO number value, and sets the printing checkbox to the default "true".
   * This method is used in the template when a user clicks into the PO form field to reset the form while retaining the PO number value.
   */
  public onLogFormReset(): void {
    const currentPOValue = this.poNumberControl.value;
    this.poValueEntered = false;
    this.logForm.reset();
    this.printingChecked = false;
    this.logForm.controls['printLabel']?.setValue(false);
    this.poNumberControl.setValue(currentPOValue);
    this.allPoContactData = new FormattedContacts();
    this.primaryContact = '';
    this.primaryContactEmail = '';
    this.notifyList = [];
    this.notifyListString = '';

    
  }

  /**
   * Handles the change event for the print checkbox.
   * This method is used in the template to update the printingChecked flag based on the checkbox state.
   *
   * @param event - The event object containing the checkbox state.
   */
  public onPrintCheckboxChange(event: any) {
    this.printingChecked = event.detail.checked; // Update the checkbox state
  }

  /**
   * Handles the change event for the job type selection.
   * This method is used in the template to update the form controls based on the selected job type.
   *
   * @param event - The event object containing the selected job type value.
   */
  public onJobTypeChange(event: any) {
    const jobType = event.detail.value;
    if (jobType === 'Project') {
      this.setProjectInitialValues();
    } else if (jobType === 'Service') {
      this.setServiceInitialValues();
    }
  }

  //--------------------------------------------------------------------------------------------------------------------------------------------//
  // Methods for handling updating contacts for the log entry

  /**
   * Updates the primary contact and sets the contact value in the form control.
   * This method is passed to the AddContactComponent.
   *
   * @param primaryContact - The updated primary contact value.
   */
  public onUpdatePrimaryContact(primaryContact: string): void {
    this.primaryContact = primaryContact;
    this.logForm.controls['contact']?.setValue(primaryContact);
  }

  /**
   * Updates the notify list and sets the notify list string in the form control.
   * This method is passed to the AddContactComponent.
   *
   * @param notifyList - The updated list of email data objects.
   */
  public onUpdateNotifyList(notifyList: PoEmailData[]): void {
    this.notifyList = notifyList;
    const notifyListString: string = this.generateNotifyListString(notifyList);
    this.notifyListString = notifyListString;
    this.logForm.controls['notify']?.setValue(notifyListString);
  }

  /**
   * Generates a comma-separated string of contact emails from the notify list.
   * This method is used in the `onUpdateNotifyList` method to create a string of contact emails.
   *
   * @param notifyList - The list of email data objects.
   * @returns A comma-separated string of contact emails.
   */
  public generateNotifyListString(notifyList: PoEmailData[]): string {
    return deriveNotifyListString(notifyList);
  }

  //--------------------------------------------------------------------------------------------------------------------------------------------//
  // Methods for activating modal popups and and other window popouts

  /**
   * Calls helper function to generates the URL for the PO report and open it in a new window
   * This method is used in the template to handle the click event for viewing the PO report.
   */
  public onViewPoReport(): void {
    //Generate the URL for the PO report and open it in a new window
    const url = getPoReportUrl(this.poValue);
    window.open(url, '_blank');
  }

  /**
   * Opens the photo manager modal.
   * This method is used in the template to open the photo manager modal.
   */
  public onShowPhotoManager() {
    this.isPhotoManagerModalOpen = true;
  }

  /**
   * Closes the photo manager modal.
   * This method is used in the template to handle the click event for closing the photo manager modal.
   */
  public onClosePhotoManager() {
    this.isPhotoManagerModalOpen = false;
  }

  /**
   * Opens the location info modal.
   * This method is used in the template to open the location info modal.
   */
  public onShowLocationInfo() {
    this.isLocaitonInfoModalOpen = true;
  }

  /**
   * Closes the location info modal.
   * This method is used in the template to handle the click event for closing location info modal.
   */
  public onCloseLocationInfo() {
    this.isLocaitonInfoModalOpen = false;
  }

  /**
   * Opens the add contact modal.
   * This method is used in the template to open the add-contact modal.
   */
  public onAddContact(): void {
    this.isAddContactModalOpen = true;
  }

  /**
   * Closes the add-contact modal.
   * This method is used in the template to handle the click event for closing the add-contact modal.
   */
  public closeAddContactModal() {
    this.isAddContactModalOpen = false;
  }


  /**
   * Closes the email preview modal.
   * This method is used in the template to handle the click event for closing the email preview modal.
   */
  public onCloseEmailPreviewModal(): void {
    this.isEmailPreviewModalOpen = false;
    this._emailService.clearEmailData();

  }

  public onPreviewLogModalOpen(): void { 
    const log = this.getLogFromForm();
    this.isPreviewLogModalOpen = true;
    // Pass the log data to the modal component
    this.previewLogModal.logData = log;
  }

  public onPreviewLogModalClose(): void {
    this.isPreviewLogModalOpen = false;
  }



  //--------------------------------------------------------------------------------------------------------------------------------------------//
  // Print configuration modal (mirrors the mobile flow)

  /** FormControl accessors so the config modal can bind the qty inputs even
   *  though it renders outside the <form> (ion-modal teleports its content). */
  public get dymoQtyControl(): FormControl {
    return this.logForm.get('printQtyDymo') as FormControl;
  }
  public get sheetQtyControl(): FormControl {
    return this.logForm.get('printQtySheet') as FormControl;
  }

  /**
   * Compact "defaults" summary shown on the print row.
   *
   * The destination half used to be the literal string "Browser", written when
   * this form could only browser-print. It has to be asked, not asserted: at a
   * facility with printers this form now sends to the print API, and a row
   * reading "Browser" beside labels coming off the Zebra is the same untruth
   * the hardcoded print method was.
   */
  public get printSummary(): string {
    if (!this.printingChecked) { return 'Off'; }
    const type = this.printTypeControl?.value || '—';
    return `${type} · ${this.printDestinationSummary}`;
  }

  /** Where the currently configured print would come out, in three words or fewer. */
  public get printDestinationSummary(): string {
    const route = this.printRoute;
    if (route.backendKind && route.browserDocs.length > 0) { return 'Printer + browser'; }
    if (route.backendKind) { return 'Sent to printer'; }
    return 'Browser';
  }

  /**
   * Where the currently configured print would go, per document — recomputed on
   * read, because the operator can change what to print, switch facility, or flag
   * a printer as down without leaving this screen.
   */
  public get printRoute(): PrintRoute {
    return this.routeFor(this.mapPrintTypeToKind(this.printTypeControl?.value) ?? 'Both');
  }

  /**
   * The route for one print kind, built from the shared context provider.
   *
   * Deferral cannot arise here in practice: the rules only defer on a mobile
   * device, and nothing navigates to this form any more — it is reachable only by
   * a bookmark (see LEGACY_DESKTOP_LOG_ROUTE) and is scheduled for removal. A
   * deferral message for a state no operator can reach would be dead copy to
   * maintain, so there is none; the documents simply do not print, exactly as
   * they would not have before.
   */
  private routeFor(kind: PrintKind): PrintRoute {
    return resolvePrintRoute(kind, this._printRouteContext.forLocation(this.currentLocation));
  }

  /**
   * Which named-printer selects are worth showing: only the documents actually
   * going to the print API. A Zebra select on a job whose label is rendering in
   * the browser would be a control with nothing behind it. Mirrors the reprint
   * modal's getters of the same name.
   */
  public get sendsZebraToBackend(): boolean {
    const backendKind = this.printRoute.backendKind;
    return backendKind === 'Zebra' || backendKind === 'Both';
  }

  public get sendsCopierToBackend(): boolean {
    const backendKind = this.printRoute.backendKind;
    return backendKind === 'Copier' || backendKind === 'Both';
  }

  /** True when any document of this job renders through the browser's print dialog. */
  public get printsInBrowser(): boolean {
    return this.printRoute.browserDocs.length > 0;
  }

  /** Names in the live printer list (a configured default not in it gets a synthetic option). */
  public get printerNames(): string[] {
    return this.availablePrinters.map((p) => p.name);
  }

  /**
   * The location currently selected in the header, from the locations config —
   * the source of this facility's printer names and QR setting.
   */
  private get currentLocation(): Location | undefined {
    return this.locations?.find((l) => l.locationLabel === this.selectedLocation?.label);
  }

  /**
   * Whether this facility puts QR codes on browser-printed labels.
   *
   * Asked only of the documents that render here — the print API path always
   * prints QR codes and never consults this. It is facility configuration, not an
   * operator choice: a QR code is only worth the space where there is a workflow
   * that scans it, and that is decided per building — now per document as well,
   * because the code scans on the copier sheet and not on the Dymo label stock.
   */
  public get browserQrLabelEnabled(): boolean {
    return resolveBrowserQrEnabled(this.currentLocation, 'label');
  }

  /** The same, for browser-printed copier sheets. */
  public get browserQrSheetEnabled(): boolean {
    return resolveBrowserQrEnabled(this.currentLocation, 'sheet');
  }

  /**
   * Whether a QR code is at stake in anything this form would browser-print —
   * the question the scale warning and the "no QR yet" note are really asking.
   *
   * Both documents, not the route's: these notes sit in the print options, which
   * the operator reads before choosing a print type, so neither the type nor the
   * route is settled yet.
   */
  public get browserQrAnyEnabled(): boolean {
    return this.browserQrLabelEnabled || this.browserQrSheetEnabled;
  }

  /**
   * States what the chosen quantities will actually produce — a quantity of 2
   * now means two copies of EACH container, so a 3-box delivery prints 6
   * labels where it used to print 2. That multiplication is a surprise the
   * first time on a machine someone is standing at, so it must be stated
   * before they print (see docs/printing.md, "Browser rendering").
   * Mirrors the mobile Review step's `printSummary` vocabulary ("Label ×3
   * each") rather than inventing a second one.
   */
  public get printCountSummary(): string {
    const facts = parseItemFacts(this.logForm?.get('qtyUnits')?.value);
    const parts: string[] = [];
    if (this.showDymoPrintQty) {
      parts.push(this.oneQtyCountSummary('Label', this.dymoQtyControl?.value, facts));
    }
    if (this.showFullSheetPrintQty) {
      parts.push(this.oneQtyCountSummary('Sheet', this.sheetQtyControl?.value, facts));
    }
    return parts.join(' · ');
  }

  private oneQtyCountSummary(
    docName: string,
    copiesEach: number,
    facts: ParsedItemFacts | null,
  ): string {
    const copies = Math.max(1, Math.floor(copiesEach) || 1);
    if (!facts) { return `${docName} ×${copies}`; }
    const total = copies * facts.quantity;
    const noun = facts.quantity === 1 ? facts.itemType : `${facts.itemType}s`;
    return `${docName}: ${copies} per container · ${facts.quantity} ${noun} = ${total} labels`;
  }

  /**
   * Opens the print-config modal: label type, quantities, and — where this
   * facility's documents actually reach the print API — which printer each goes
   * to.
   *
   * The printer list is fetched only when something is backend-bound. On the
   * browser path the destination belongs to the browser's own print dialog, so a
   * list of API printer names would be a control with nothing behind it.
   */
  public openPrintConfig(): void {
    if (!this.printingChecked || this.printTypeControl?.value == null) {
      return;
    }

    const location = this.currentLocation;
    if (!this.selectedZebraPrinter) {
      this.selectedZebraPrinter = location?.zebraPrinterName || '';
    }
    if (!this.selectedCopierPrinter) {
      this.selectedCopierPrinter = location?.copierPrinterName || '';
    }

    if (this.printRoute.backendKind && this.availablePrinters.length === 0) {
      this._printService.fetchPrinters();
    }

    this.isPrintConfigModalOpen = true;
  }

  public onClosePrintConfig(): void {
    this.isPrintConfigModalOpen = false;
  }

  /** Print action from within the config modal: validate, print, then close. */
  public onPrintFromConfig(): void {
    if (!this.arePrintQtyControlsValid()) {
      return;
    }
    const newLog = this.getLogFromForm();
    this.newLog = newLog;
    this.callPrintLabel(newLog);
    this.onClosePrintConfig();
  }

  /**
   * Checks the print quantity fields and generates print labels accordingly.
   * Generates a print label with the log data without submitting the form.
   * This method is used in the template to handle the click event for printing only.
   */
  public onPrintOnly(): void {
    //Check the validity of the print qty controls
    if (!this.arePrintQtyControlsValid()) {
      return; //Exit if the controls are invalid
    }


    const newLog = this.getLogFromForm();
    this.newLog = newLog;
    this.callPrintLabel(newLog);
  }


  //--------------------------------------------------------------------------------------------------------------------------------------------//
  // Methods for handling email generation and sending


 

  public handleTogglePhotosInEmail(event: any): void {
    this.includePhotosInEmail = event.detail.checked;
  }

  public handleToggleEmailsDisabled(event: any): void {
    this.emailsDisabled = event.detail.checked;
  }

  /**
   * Generates an email with the log data without submitting the form.
   * This method is used in the template to handle the click event for generating an email only.
   */
  public async onGenerateEmailOnly(): Promise<void> {
    this.isEmailPreviewModalOpen = true;
    
    
      const newLog = this.getLogFromForm();
      this.newLog = newLog;
      const newAddReceivingLogRequest = this.prepareLogForSubmission(newLog);
      this.addLogRequest = newAddReceivingLogRequest;
      
      // Ensure emailData exists
      if (!newAddReceivingLogRequest.emailData) {
        throw new Error('Email data is required for preview');
      }

      // Create the email preview request
      const emailPreviewRequest = new EmailPreviewRequest(
        newAddReceivingLogRequest.receivingLogRow,
        newAddReceivingLogRequest.emailData
      );

      // Generate email preview
      this._emailService.generateEmailPreview(emailPreviewRequest);
  
  }
  /**
   * Sends an email with the log data without submitting the form.
   * This method is used in the template to handle the click event for sending an email only.
   */
  public onSendEmail(): void {
  
    const request = this.addLogRequest;
    if (request.emailData) {
      request.emailData.includePhotosInEmail = this.includePhotosInEmail;
    } else{
      request.emailData = new EmailData();
      request.emailData.includePhotosInEmail = this.includePhotosInEmail;
      request.emailData.userEmail = this.userEmail || '';
    }
    const photos = this.includePhotosInEmail ? this._photoService.getLogEntryPhotos() : [];
    // Define the success function to handle successful log submission
    const onSuccessFunction = () => {
      this.onCloseEmailPreviewModal();
    };
    
    this._emailService.sendEmail(request, photos, onSuccessFunction);
  }

 
  //--------------------------------------------------------------------------------------------------------------------------------------------//
  // Methods for handling Log Entry Photo Management
 

  public onTogglePhotoInclusion(photosDisabled: boolean): void {
    this.photosDisabled = photosDisabled;
    if (photosDisabled) {
      this.onClosePhotoManager();
    }
  }

  //--------------------------------------------------------------------------------------------------------------------------------------------//
  // Methods for handling printing and email notifications

  /**
   * Calls the appropriate print service based on the selected print type.
   * This method is used in the `onPrintOnly` and `handleSuccess` methods to print the log data.
   *
   * @param logData - The log data to be printed.
   * @param itemIds - Receiving item row GUIDs from the submit response (absent
   *   on the pre-submit "Print" path, which has no item rows yet → no QR).
   */
  public callPrintLabel(logData: NewLog, itemIds?: string[]): void {
    // Print the contact's real name — from the employee directory when it lists
    // this address, else the PO's BambooHR insight, and only picking a name out of
    // the address itself when neither source knows the person (group address,
    // external, or BambooHR unavailable).
    if (logData.contact) {
      logData.contact = this._contactsService.resolveName(
        logData.contact,
        findContactInsight(this.allPoContactData, logData.contact),
      );
    }

    const kind = this.mapPrintTypeToKind(logData.printType);
    if (!kind) {
      this._toastService.showWarningMessage('Select a print type before printing.');
      return;
    }

    // Where each document of THIS job goes. A job can split: with the Zebra
    // reported down at a facility that has both printers, the label renders in
    // the browser while the copier sheet still goes to the print API. Both halves
    // below have to fire.
    const route = this.routeFor(kind);

    if (route.backendKind) {
      this.printViaApi(logData, route.backendKind, itemIds);
    }

    // Documents this surface cannot print AT ALL: the backend is ruled out for
    // them (a printer reported down) and the browser's print dialog reaches no
    // printer from a phone. resolvePrintRoute hands these back deliberately
    // rather than dropping them, because the caller owes the operator a message
    // naming what to print and where — and this form was the one caller that
    // never said anything. The copier sheet simply did not appear: no dialog, no
    // log line, no error, and a successful-looking label print beside it.
    if (route.deferredDocs.length > 0) {
      const docs = route.deferredDocs.includes('label') && route.deferredDocs.includes('sheet')
        ? 'Labels and copier sheets'
        : route.deferredDocs.includes('label') ? 'Labels' : 'Copier sheets';
      this._toastService.showWarningMessage(
        `${docs} can only be printed from a desktop computer. Open the log table on a desktop `
          + 'computer and reprint them there.',
        6000,
      );
    }

    if (route.browserDocs.length === 0) {
      return;
    }

    // Per-container browser labels: one label per physical item, each carrying its
    // own QR code and its own "BOX n of N" caption. With no GUIDs — the pre-submit
    // "Print" button, which has no item rows yet — this stays undefined and the
    // service prints the legacy repeat-one-label document.
    //
    // Positional numbering is correct HERE and only here: this form parses one
    // (quantity, type) pair out of its free-text Quantity Units field and cannot
    // mix container types. Mobile has stored rows and passes those instead.
    const itemRefs = buildSingleTypeItemRefs(
      itemIds,
      parseItemFacts(logData.qtyUnits)?.itemType ?? 'item',
    );
    // Uniform copies, applied per item — the same meaning the API path gives these
    // quantities (see printViaApi), so switching paths during an outage changes
    // where the labels come out, not how many.
    // includeQr is asked PER DOCUMENT: a facility can have QR off on the Dymo
    // label, where it does not scan at the stock in use, and on for the copier
    // sheet, where it does.
    const itemsFor = (copiesEach: number, doc: PrintDoc): BrowserLabelItems | undefined =>
      itemRefs.length > 0
        ? {
            items: itemRefs,
            appBaseUrl: QR_BASE_URL,
            copies: itemRefs.map(() => copiesEach),
            includeQr: doc === 'label' ? this.browserQrLabelEnabled : this.browserQrSheetEnabled,
          }
        : undefined;

    // Which documents render here is the ROUTE's answer, not the print type's:
    // under "Zebra is down" a "Both" job browser-prints only the label, because
    // the copier sheet has already gone to the copier through the API above.
    if (route.browserDocs.includes('label')) {
      this._printService.printDymoLabel(logData, itemsFor(logData.printQtyDymo, 'label'));
    }
    if (route.browserDocs.includes('sheet')) {
      this._printService.printLargeLabel(logData, itemsFor(logData.printQtySheet, 'sheet'));
    }
  }

  /**
   * Auto-print path: build a {@link PrintRequest} from the current form values
   * and submit it to the backend print API. Because the desktop can print
   * before the log is submitted (the "Print" button), this uses the full
   * `POST /V1/print` (form values) rather than the `/from-log/{id}` endpoint.
   *
   * Printer names come from the config modal's selection when set, otherwise
   * the selected location's config (the same source the mobile flow uses); a
   * missing printer is surfaced to the user.
   *
   * @param logData The log data to print.
   * @param kind The kind the ROUTE left on the backend — not the kind the
   *   operator chose. With the Zebra reported down and "Both" selected that is
   *   'Copier', and re-deriving it from `printType` here would send the label
   *   straight back to the printer this device has just been told is dead.
   * @param itemIds Receiving item row GUIDs from the submit response. When
   *   present, each becomes a per-item QR URL on the request; when absent
   *   (pre-submit printing), the request stays on the legacy no-QR path.
   */
  public printViaApi(logData: NewLog, kind: PrintKind, itemIds?: string[]): void {
    const location = this.currentLocation;
    const needsZebra = kind === 'Zebra' || kind === 'Both';
    const needsCopier = kind === 'Copier' || kind === 'Both';
    const zebraPrinterName = this.selectedZebraPrinter || location?.zebraPrinterName || '';
    const copierPrinterName = this.selectedCopierPrinter || location?.copierPrinterName || '';

    // A backstop, not a routine path: the route only puts a document on the
    // backend where the facility has that printer configured, so reaching this
    // means the name was blanked after the route was resolved. Naming the Printing
    // screen is the only actionable thing to say — the browser/API choice this
    // message used to offer is not a control any more.
    if ((needsZebra && !zebraPrinterName) || (needsCopier && !copierPrinterName)) {
      this._toastService.showWarningMessage(
        'No printer name is set for this location, so nothing was sent. Pick a printer in Print Settings.'
      );
      return;
    }

    const request: PrintRequest = {
      kind,
      zebraPrinterName: needsZebra ? zebraPrinterName : undefined,
      copierPrinterName: needsCopier ? copierPrinterName : undefined,
      jobNumber: logData.jobNumber,
      poNumber: logData.poNumber,
      labelCount: logData.printQtyDymo,
      copyCount: logData.printQtySheet,
      vendor: logData.vendor,
      jobName: logData.jobName,
      jcDept: logData.jcDept,
      type: logData.type,
      qtyUnits: logData.qtyUnits,
      shelfLocation: logData.shelfLocation,
      shopSqFt: logData.shopSqFt ?? null,
      estimatedDateOut: this.toIsoDate(logData.estimatedDateOut),
      notify: logData.notify,
      contact: logData.contact,
      items: logData.items,
      notes: logData.notes,
      dateIn: this.toIsoDate(logData.dateIn),
      // Per-item QR content ({appBaseUrl}/view-logged-item?payload={guid}),
      // with the unit noun parsed from the free-text Quantity Units field the
      // same way the submit derived it. Undefined (legacy no-QR path) when no
      // GUIDs exist — e.g. pre-submit printing.
      //
      // Single-type: the desktop form parses ONE (quantity, type) pair out of its
      // free-text field and submits the legacy `itemQuantity`/`itemType` scalars,
      // so the job-level noun plus positional numbering is correct here. Mixed
      // container types are a mobile-only capability (and a desktop-overhaul job).
      qr: buildSingleTypePrintQr(
        QR_BASE_URL,
        itemIds ?? [],
        parseItemFacts(logData.qtyUnits)?.itemType ?? 'item',
      ),
    };

    // With QR items, the print quantities mean copies of EACH item's document.
    // The desktop has uniform quantities only; the split rule expands them to
    // per-item copies and separates the Zebra/Copier requests when the label
    // and sheet quantities differ. Legacy no-QR requests pass through as one.
    const itemCount = request.qr?.items.length ?? 0;
    const requests = splitQrPrintRequests(
      request,
      Array(itemCount).fill(logData.printQtyDymo),
      Array(itemCount).fill(logData.printQtySheet),
    );
    this._printService.submitPrintJobBatch(requests);
  }

  /**
   * Maps a desktop print-type option label to the print API's PrintKind:
   * Label → Zebra, Copier Sheet → Copier, Both → Both. ("Label" prints on the
   * Zebra in auto mode and renders a Dymo label in browser mode.)
   */
  private mapPrintTypeToKind(printType: string | undefined): PrintKind | null {
    if (!printType) {
      return null;
    }
    if (printType === this.printTypeOptions[2].label) {
      return 'Both';
    }
    if (printType === this.printTypeOptions[0].label) {
      return 'Zebra';
    }
    if (printType === this.printTypeOptions[1].label) {
      return 'Copier';
    }
    return null;
  }

  /**
   * Converts a date to the ISO `YYYY-MM-DD` string the print API expects (it
   * rejects empty strings). Accepts a Date or an `MM/DD/YYYY` string; returns
   * null for anything unparseable.
   */
  private toIsoDate(value: Date | string | null | undefined): string | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      const y = value.getFullYear();
      const m = String(value.getMonth() + 1).padStart(2, '0');
      const d = String(value.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    const parts = value.split('/');
    if (parts.length !== 3) {
      return null;
    }
    const [m, d, y] = parts;
    if (!m || !d || !y) {
      return null;
    }
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }


  /** Checks the validity of the print qty controls */

  public arePrintQtyControlsValid(): boolean {

    console.log('printQtyDymo control', this.logForm.controls['printQtyDymo']);

     //If both print quantities are required and either is invalid, mark them as touched and return
      if (         this.showBothPrintQty === true  &&  (!this.logForm.controls['printQtyDymo']?.valid || !this.logForm.controls['printQtySheet']?.valid)
      ) {
          this.logForm.controls['printQtyDymo']?.markAsTouched();
          this.logForm.controls['printQtySheet']?.markAsTouched();
          this._toastService.showWarningMessage('Please enter valid print quantities for both Dymo and Full Sheet.');
          return false;
      } 
    //If the dymo print quantity is required and invalid, mark it as touched and return
    if (         this.showDymoPrintQty === true  &&  !this.logForm.controls['printQtyDymo']?.valid)
      {
        this.logForm.controls['printQtyDymo']?.markAsTouched();
        this._toastService.showWarningMessage('Please enter a valid print quantity for Dymo.');
        return false;
      } 
      //If the full sheet print quantity is required and invalid, mark it as touched and return
      if (         this.showFullSheetPrintQty === true  &&  !this.logForm.controls['printQtySheet']?.valid)
      {
        this.logForm.controls['printQtySheet']?.markAsTouched();
        this._toastService.showWarningMessage('Please enter a valid print quantity for Full Sheet.');
        return false;
      } 
      return true;
  }


  /**
   * Prepares email message data for sending or preview.
   *
   * @returns emailData object if successful, throws an error if required data is missing
   */
  private prepareEmailMessageData(): EmailData | null {
    // Determine the log type based on the project or service flag
    const logType = this.isProject
      ? LogType.Project
      : this.isService
      ? LogType.Service
      : null;

    // Build the email message data from the notify list, location config, and log type
    const result = buildEmailData({
      notifyListString: this.notifyListString,
      locations: this.locations,
      selectedLocationLabel: this.selectedLocation?.label,
      logType: logType,
      userEmail: this.userEmail,
    });

    if (!result.ok) {
      const failureMessages: Record<BuildEmailDataFailure, string> = {
        locationNotFound: 'Location not found',
        noLogType: 'Neither Project nor Service type is selected',
        noRecipients: 'No Email Recipients Specified',
      };
      this._toastService.showWarningMessage(failureMessages[result.reason]);
      this.onCloseEmailPreviewModal();
      return null;
    }

    return result.emailData;
  }

  //--------------------------------------------------------------------------------------------------------------------------------------------//
  // Methods for submiting a new log entry

  /**
   * Submits the log form and processes the log data.
   * This method is used in the template to handle the form submission event.
   */
  public onReviewLog(): void {
    // Check if the form is valid
    if (!this.logForm.valid) {
      // Mark all controls as touched to trigger validation feedback
      this.logForm?.markAllAsTouched();
      const errorMsg = this.toastText.logForm.invalidFormError;
      // Show a warning message if the form is invalid
      this._toastService.showWarningMessage(errorMsg);
      return;
    }
    const logEntryPhotos = this._photoService.getLogEntryPhotos();
    if (!this.photosDisabled && logEntryPhotos.length === 0) {
      this._toastService.showWarningMessage(
        this.toastText.photos.upload.noPhotosError
      );
      this.onShowPhotoManager();
      return;
    }

    // Extract the log data from the form
    const newLog = this.getLogFromForm();
    this.newLog = newLog;
    const newAddReceivingLogRequest = this.prepareLogForSubmission(newLog);
    this.addLogRequest = newAddReceivingLogRequest;

   // Create the email preview request if the email data is present
   if (newAddReceivingLogRequest.emailData && !this.emailsDisabled)
   {
   const emailPreviewRequest = new EmailPreviewRequest(
    newAddReceivingLogRequest.receivingLogRow,
    newAddReceivingLogRequest.emailData
  );

  // Generate email preview
  this._emailService.generateEmailPreview(emailPreviewRequest);
  }
    // Show the preview modal
    this.onPreviewLogModalOpen();
  }



  /**
   * Held on an empty directory first: a submit with no employee records
   * would save a log whose contact name can't be resolved and whose labels
   * print wrong, so this refetches and blocks rather than letting the
   * submit build against a directory known to be empty.
   */
  public onSubmitLog(): void {
    if (this._contactsService.fetchedEmployees.value.length === 0) {
      this._receivingLogService.dataLoading.next({
        isLoading: true,
        message: 'Checking employee list…',
      });
      this._contactsService.ensureDirectoryPopulated().subscribe({
        next: () => {
          this._receivingLogService.dataLoading.next({ isLoading: false, message: '' });
          this._submitLog();
        },
        error: (err: EmployeeDirectoryError) => {
          this._receivingLogService.dataLoading.next({ isLoading: false, message: '' });
          // The service has already toasted `err.message`; this repeats it next
          // to the submit action rather than only on the toast, which may have
          // already dismissed by the time the user looks back.
          this._toastService.showWarningMessage(err.message);
        },
      });
      return;
    }
    this._submitLog();
  }

  private _submitLog(): void {
    const addLogRequest = this.addLogRequest;
    if (this.emailsDisabled) {
      addLogRequest.emailData = undefined;
    }
    if (addLogRequest.emailData) {
      addLogRequest.emailData.includePhotosInEmail = this.includePhotosInEmail;
    }

    const logEntryPhotos = this._photoService.getLogEntryPhotos();

    // Submit the log data via the log submission service
    this._receivingLogService.dataLoading.next({
      isLoading: true,
      message: this.loadingText.submittingLog,
    });
    this._logSubmissionService.submit(addLogRequest, logEntryPhotos).subscribe({
      next: async (response) => {
        this._receivingLogService.dataLoading.next({ isLoading: false, message: '' });
        // Async mode (AsyncSubmit flag ON): the core log is saved and the slow work
        // (note / photo uploads / emails) is enqueued as background jobs. The per-step
        // statuses are Pending, not failures, so don't surface them — just confirm and
        // close. (Desktop has no polling UI; the phone flow owns status resolution.)
        if (response.outcome === 'Accepted') {
          await this._toastService.showSuccessMessage('Received — finishing up in the background.');
          this.handleLogSuccess(this.newLog, response);
          this.onPreviewLogModalClose();
          return;
        }
        // Synchronous mode: surface the per-step statuses (log insert, email, photos) as toasts
        await this._toastService.processLogResponse(response);
        // The item-rows write is a non-fatal sub-operation of the log insert —
        // surface a failure as a warning without blocking the success flow.
        if (response.receivingItemsStatus && !response.receivingItemsStatus.isSuccess) {
          this._toastService.showWarningMessage(
            `Item QR codes failed: ${response.receivingItemsStatus.message}`
          );
        }
        if (response.isSuccess) {
          this.handleLogSuccess(this.newLog, response);
          this.onPreviewLogModalClose();
        }
      },
      error: (err: LogSubmissionError) => {
        console.error(err.cause ?? err);
        this._receivingLogService.dataLoading.next({ isLoading: false, message: '' });
        this._toastService.showWarningMessage(err.userMessage);
      },
    });
  }

  /**
   * Prepares the log data for submission to the log service.
   * This method is used in the `onSubmitLog` method to prepare the log data for submission.
   *
   * @param newLog - The log data to be prepared for submission.
   * @returns A new AddReceivingLogRequest object.
   */
  private prepareLogForSubmission(newLog: NewLog): AddReceivingLogRequest {
    // Create a new ReceivingLogRow object based on the log data
    const newReceivingLogRow = createReceivingLogRow(newLog, {
      locationLabel: this.selectedLocation.label || '',
      loggedBy: this.username || '',
    });

    // If the job type is "Service", set the dateOut and takenBy fields
    if (this.isService) {
      applyServiceSubmissionFields(newReceivingLogRow, newLog.shelfLocation);
    }

    this._receivingLogService.setDateFriendlyLogRowData(newReceivingLogRow);
    //convert newReceivingLogRow to ApiReceivingLogRow, and set the log row data in the log service
    const newApiReceivingLogRow = new ApiReceivingLogRow(newReceivingLogRow);
    this._receivingLogService.setLogRowData(newApiReceivingLogRow);

    //create a new AddReceivingLogRequest object
    const newAddReceivingLogRequest = new AddReceivingLogRequest(
      newApiReceivingLogRow
    );

    // Discrete item facts for the QR item rows, parsed from the free-text
    // "Quantity Units" field ("3 boxes" → 3 / "box"). When the value doesn't
    // describe a unit count (blank, zero, or a measurement like "500 ft"),
    // both fields stay unset and the backend creates no item rows (legacy).
    const itemFacts = parseItemFacts(newLog.qtyUnits);
    if (itemFacts) {
      newAddReceivingLogRequest.itemQuantity = itemFacts.quantity;
      newAddReceivingLogRequest.itemType = itemFacts.itemType;
    }

    if (!this.emailsDisabled) {
      const emailData = this.prepareEmailMessageData();
      if (emailData) {
        newAddReceivingLogRequest.emailData = emailData;
      }
    }

    return newAddReceivingLogRequest;
  }

  /**
   * Handles the success event after submitting the log.
   * The log form is reset, the log modal is closed, a print window and an email is opened with the log data.
   * This method is used in the `onSubmitLog` method to handle the success event after the log is submitted.
   *
   * @param log - The log data that was successfully submitted.
   * @param response - The submit response, carrying the receiving item row
   *   GUIDs used to build the per-item QR URLs on the auto-print path.
   */
  private async handleLogSuccess(log: NewLog, response?: AddReceivingLogResponse): Promise<void> {

    this.poValueEntered = false;
    this.logForm.reset();
    this.isLogModalOpen = false;

    // Wait for toasts to be shown (approximately 3 seconds)
    if (this.printingChecked) {
      const itemIds = response?.receivingItemIds;
      // See docs/printing.md, "Browser rendering": a QR-less label
      // otherwise reads as a fault rather than as how this log was recorded.
      // Task 3: this print fires automatically with no config screen of its
      // own, so the actual-size instruction has to ride along here — the
      // print dialog is the only screen the operator sees before it opens.
      // Only said where this facility prints QR codes at all: at one that does
      // not, "no QR codes" describes the normal label and reads as a fault.
      const noQrNote = this.browserQrAnyEnabled && (!itemIds || itemIds.length === 0)
        ? 'This log has no tracked items, so what prints — labels, copier sheets, or both — comes out without QR codes. '
        : '';
      // The actual-size reminder only applies to the browser-rendered iframe
      // print dialog, not the backend print-API path. That used to be a constant
      // ("desktop always browser-prints"); it is now a property of THIS job, and
      // a split job still earns it for the half that renders here.
      // Worth giving either way — a scaled-down label is undersized for the
      // stock — but only the QR makes it urgent, so the reason changes with it.
      // Asked of the documents ACTUALLY rendering here: at a facility that has
      // dropped QR from its Dymo labels but kept it on its copier sheets, a
      // label-only browser job has no code to shrink, and a sheet still does.
      const browserDocs = this.routeFor(this.mapPrintTypeToKind(log.printType) ?? 'Both')
        .browserDocs;
      const sizeNote = browserDocs.length === 0
        ? ''
        : anyBrowserQrEnabled(this.currentLocation, browserDocs)
          ? 'In the print dialog, set Scale to "Actual size" (100%) or the QR won\'t scan.'
          : 'In the print dialog, set Scale to "Actual size" (100%) or it prints undersized.';
      if (noQrNote || sizeNote) {
        this._toastService.showWarningMessage(`${noQrNote}${sizeNote}`.trim(), 6000);
      }
      setTimeout(() => {
        this.callPrintLabel(log, itemIds);
      }, 3000);
    }
    
    this.poData = new FormattedPoValues();
    this.poValueHasLetters = false;
    this.photosDisabled = false;
    this.includePhotosInEmail = true;
    this._emailService.emailMessageData.next(new EmailPreviewResponse());
    this.addLogRequest = new AddReceivingLogRequest(new ApiReceivingLogRow(), new EmailData());
   this._photoService.updateLastLogEntryPhotos();
  this._photoService.clearLogEntryPhotos();
  }

  /**
   * Extracts the log data from the form controls and creates a new NewLog object.
   * This method is used in the `onGenerateEmailOnly`, `onPrintOnly`, and `onSubmitLog` methods to get the log data from the form.
   *
   * @returns A new NewLog object with the form data.
   */
  private getLogFromForm(): NewLog {
    const formControls = this.logForm.controls; // Access form controls
    const newLogData: { [key: string]: any } = {}; // Initialize an object to hold form values

    Object.keys(formControls).forEach((key) => {
      newLogData[key] = formControls[key].value; // Assign each control's value to the object
    });

    // Add any extra static values or transformations as needed
    newLogData['poNumber'] = this.poValue;
  
    const newLog = new NewLog(newLogData);


    // Return a new instance of NewLog with the dynamically constructed object
    return newLog;
  }

  

  //THIS METHOD IS NOT CURRENTLY USED
  /**
   * Subscribes to changes in the 'type' form control and sets initial values
   * based on whether the selected type is 'Project' or 'Service'.
   *
   * When the 'type' control value changes:
   * - If the value is 'Project', it triggers setting initial values specific to a project.
   * - If the value is 'Service', it triggers setting initial values specific to a service.
   */
  // public setFormControlSubscriptions(): void {
  //   this.typeControlSubscription = this.typeControl?.valueChanges.subscribe(
  //     (value: string) => {
  //       if (value === 'Project') {
  //         this.setProjectInitialValues();
  //       } else if (value === 'Service') {
  //         this.setServiceInitialValues();
  //       }
  //     }
  //   );
  // }
}
